import React, { useContext, useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import {
  MY_ASSIGNMENTS_QUERY, MY_ASSIGNED_LESSONS_QUERY, GET_MEETING_WITH_CONTENT_QUERY,
  REVIEW_SUBMISSION_MUTATION, UNASSIGN_LESSON_MUTATION,
  DRILL_RESULTS_FOR_ME_QUERY, MY_SHARED_RESULTS_QUERY, COMMENT_DRILL_RESULT_MUTATION,
  MY_STUDENTS_QUERY, MY_TEACH_CODE_QUERY, MY_TEACHER_LINKS_QUERY,
  LINK_TEACHER_MUTATION, SET_TEACHER_LANGUAGES_MUTATION,
  REGENERATE_TEACH_CODE_MUTATION, UNLINK_TEACHER_MUTATION, REMOVE_STUDENT_MUTATION,
} from '../query/gqlQueries.js'
import styles from './AddProjectDialog.module.scss'
import StudentLessonView from '../UI/StudentLessonView'
import StudentDashboard from '../UI/StudentDashboard'
import { DrillMisses, drillModeLabel } from '../UI/DrillDetails'
import useLearnerContext from '../hooks/useLearnerContext'
import CloseIcon from '@mui/icons-material/Close'

// Homework, both ways round:
//   My homework  — lessons assigned TO me; open one, do it, record the speaking answers.
//   My students  — lessons I assigned; read each student's answers, leave feedback.
const STATUS = { assigned: ['To do', '#fbbf24'], submitted: ['Submitted', '#60a5fa'], reviewed: ['Reviewed', '#6ee7b7'] }
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '')
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '')
const card = { background: '#141418', border: '1px solid #2a2a33', borderRadius: 12, padding: '0.75rem 0.9rem' }
const btn = { background: '#1b1b21', color: '#e8e8ec', border: '1px solid #2a2a33', borderRadius: 9, padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem' }
const primary = { ...btn, background: '#34d399', color: '#06281d', border: 'none', fontWeight: 700 }

const Chip = ({ status }) => { const [label, color] = STATUS[status] || STATUS.assigned; return <span style={{ color, border: `1px solid ${color}55`, background: `${color}18`, borderRadius: 999, padding: '0.12rem 0.55rem', fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span> }

export default function HomeworkDialog({ voiceSpeed }) {
  const { dialogStates, closeDialog, openDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)
  const isOpen = dialogStates['dialogHomework']
  const [tab, setTab] = useState('mine')
  const [lesson, setLesson] = useState(null) // { lesson, meetingId } → StudentLessonView
  const [openErr, setOpenErr] = useState('')
  const [student, setStudent] = useState(null) // username → StudentDashboard

  // None of these passes a username: the server takes it from the token.
  const mine = useQuery(MY_ASSIGNMENTS_QUERY, { variables: {}, skip: !isOpen || !username, fetchPolicy: 'network-only', errorPolicy: 'all' })
  const teach = useQuery(MY_ASSIGNED_LESSONS_QUERY, { variables: {}, skip: !isOpen || !username, fetchPolicy: 'network-only', errorPolicy: 'all' })
  const [fetchMeeting] = useLazyQuery(GET_MEETING_WITH_CONTENT_QUERY, { fetchPolicy: 'network-only' })
  // Practice sets students shared with me / that I shared with a teacher.
  const drillsIn = useQuery(DRILL_RESULTS_FOR_ME_QUERY, { variables: { limit: 50 }, skip: !isOpen || !username, fetchPolicy: 'network-only', errorPolicy: 'ignore' })
  const drillsOut = useQuery(MY_SHARED_RESULTS_QUERY, { variables: { limit: 50 }, skip: !isOpen || !username, fetchPolicy: 'network-only', errorPolicy: 'ignore' })

  if (!isOpen) return null
  const close = () => closeDialog('dialogHomework')
  const backendOld = /Cannot query field|Unknown type/i.test((mine.error && mine.error.message) || '')

  const openLesson = async (meetingId) => {
    setOpenErr('')
    try {
      const r = await fetchMeeting({ variables: { identity: meetingId } })
      const raw = r.data?.getMeetingWithContent?.meeting?.properties?.lessonData
      const l = JSON.parse(raw || 'null')
      if (l && Array.isArray(l.sections)) setLesson({ lesson: l, meetingId })
      else setOpenErr('This lesson has no content yet.')
    } catch (e) { setOpenErr(e.message) }
  }

  if (student) {
    // Opening the importer from the dashboard closes the dashboard, so the
    // Import Lesson dialog is not stranded underneath a full-screen overlay.
    return (
      <StudentDashboard
        student={student}
        onClose={() => { setStudent(null); teach.refetch() }}
        onOpenImport={() => { setStudent(null); close(); openDialog('dialogImportLesson') }}
      />
    )
  }

  if (lesson) {
    return ReactDOM.createPortal(
      <StudentLessonView lesson={lesson.lesson} meetingId={lesson.meetingId} voiceSpeed={voiceSpeed} homework onClose={() => { setLesson(null); mine.refetch(); teach.refetch() }} />,
      document.body
    )
  }

  const assignments = mine.data?.myAssignments || []
  const taught = teach.data?.myAssignedLessons || []
  const tabStyle = (t) => ({ ...btn, borderColor: tab === t ? '#34d399' : '#2a2a33', color: tab === t ? '#6ee7b7' : '#8a8a95' })

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={close}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: '680px' }}>
        <button className={styles.closeBtn} onClick={close}><CloseIcon /></button>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Lessons</div>
            <h2 className={styles.title}>Homework</h2>
          </div>
        </div>
        <div className={styles.container}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem' }}>
            <button style={tabStyle('mine')} onClick={() => setTab('mine')}>My homework{assignments.length ? ` (${assignments.filter(a => a.status === 'assigned').length} to do)` : ''}</button>
            <button style={tabStyle('teach')} onClick={() => setTab('teach')}>My students{taught.length ? ` (${taught.length})` : ''}</button>
          </div>

          {backendOld && <div style={{ color: '#fbbf24', marginBottom: '0.8rem' }}>The GraphQL server hasn't been redeployed with the homework schema yet.</div>}
          {openErr && <div style={{ color: '#f87171', marginBottom: '0.6rem' }}>{openErr}</div>}

          {/* Who you are linked to. A teacher hands out a code; the student
              enters it. Nobody is ever picked from a list of all usernames. */}
          {!backendOld && (tab === 'mine' ? <MyTeachers /> : <MyTeachCode onOpenStudent={setStudent} />)}

          {tab === 'mine' && (
            mine.loading ? <div style={{ color: '#8a8a95' }}>Loading…</div>
              : assignments.length === 0 ? <div style={{ color: '#8a8a95', padding: '1rem 0', textAlign: 'center' }}>Nothing assigned to you yet.</div>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
                  {assignments.map(a => (
                    <div key={a.meetingId} style={{ ...card, display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ color: '#e8e8ec', fontWeight: 600 }}>{a.title}</div>
                        <div style={{ color: '#8a8a95', fontSize: '0.82rem' }}>from <b style={{ color: '#c3c3cc' }}>{a.teacher}</b>{a.due ? ` · due ${fmtDate(a.due)}` : ''}{a.submission?.updatedAt ? ` · saved ${fmtTime(a.submission.updatedAt)}` : ''}</div>
                        {a.submission?.comment && <div style={{ marginTop: '0.35rem', color: '#fbbf24', fontSize: '0.85rem' }}>💬 {a.submission.comment}</div>}
                      </div>
                      <Chip status={a.status} />
                      <button style={primary} onClick={() => openLesson(a.meetingId)}>{a.status === 'assigned' ? 'Start' : 'Open'}</button>
                    </div>
                  ))}
                </div>
          )}

          {tab === 'teach' && (
            teach.loading ? <div style={{ color: '#8a8a95' }}>Loading…</div>
              : taught.length === 0 ? <div style={{ color: '#8a8a95', padding: '1rem 0', textAlign: 'center' }}>You haven't assigned any lessons. Open a lesson in <em>Past Lessons</em> and press <b>Assign as homework</b>.</div>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', maxHeight: '60vh', overflowY: 'auto' }}>
                  {taught.map(t => (
                    <div key={t.meetingId} style={card}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.6rem', alignItems: 'baseline', marginBottom: '0.4rem' }}>
                        <div style={{ color: '#e8e8ec', fontWeight: 600 }}>{t.title}</div>
                        <div style={{ color: '#8a8a95', fontSize: '0.8rem' }}>{t.due ? `due ${fmtDate(t.due)} · ` : ''}{t.students.filter(s => s.status !== 'assigned').length}/{t.students.length} submitted</div>
                      </div>
                      {t.students.map(s => <StudentRow key={s.student} row={s} meetingId={t.meetingId} onChanged={() => teach.refetch()} />)}
                    </div>
                  ))}
                </div>
          )}
          {/* Practice sets (e.g. Russian cases) shared between student and teacher. */}
          {tab === 'teach' && (
            <div style={{ marginTop: '1.1rem' }}>
              <div style={sectionLabel}>Practice results shared with you</div>
              {(drillsIn.data?.drillResultsForMe || []).length === 0
                ? <div style={{ color: '#8a8a95', fontSize: '0.85rem' }}>Nothing yet. A student can send a finished set from <em>Special Mode</em> when they reach the results card.</div>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '34vh', overflowY: 'auto' }}>
                  {drillsIn.data.drillResultsForMe.map(d => <DrillRow key={d.id} d={d} onChanged={() => drillsIn.refetch()} />)}
                </div>}
            </div>
          )}

          {tab === 'mine' && (drillsOut.data?.mySharedResults || []).length > 0 && (
            <div style={{ marginTop: '1.1rem' }}>
              <div style={sectionLabel}>Practice you shared</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '30vh', overflowY: 'auto' }}>
                {drillsOut.data.mySharedResults.map(d => (
                  <div key={d.id} style={card}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
                      <span style={{ color: '#e8e8ec', flex: 1, minWidth: 160 }}>{d.title} <span style={{ color: '#8a8a95' }}>→ {d.teacher}</span></span>
                      <span style={{ color: pctColor(d.pct), fontWeight: 700 }}>{d.pct}%</span>
                      <span style={{ color: '#8a8a95', fontSize: '0.78rem' }}>{fmtTime(d.sharedAt)}</span>
                    </div>
                    {d.comment && <div style={{ marginTop: '0.35rem', color: '#fbbf24', fontSize: '0.85rem' }}>💬 {d.comment}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

const sectionLabel = { color: '#8a8a95', fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '0.5rem' }
const pctColor = (p) => (p >= 80 ? '#6ee7b7' : p >= 50 ? '#fbbf24' : '#f87171')
const linkCard = { ...card, marginBottom: '0.9rem' }
// On = this teacher can see that language. Off = they cannot.
const langChip = (on) => ({
  borderRadius: 999, padding: '0.18rem 0.6rem', fontSize: '0.78rem', cursor: 'pointer',
  background: on ? 'rgba(52,211,153,0.14)' : '#18181c',
  border: `1px solid ${on ? 'rgba(52,211,153,0.45)' : '#2a2a33'}`,
  color: on ? '#6ee7b7' : '#8a8a95',
})
const personRow = { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.35rem 0', borderTop: '1px solid #1f1f26' }

// ── The student's side: add a teacher by typing the code they gave you ───────
// Entering a code is the only way a link is made, so joining is always the
// student's own decision and no one can attach themselves to you uninvited.
function MyTeachers() {
  const { username } = useContext(UserContext)
  const { learning } = useLearnerContext(username)
  const { data, refetch } = useQuery(MY_TEACHER_LINKS_QUERY, { variables: {}, fetchPolicy: 'network-only', errorPolicy: 'ignore' })
  const [link, { loading }] = useMutation(LINK_TEACHER_MUTATION)
  const [setLangs] = useMutation(SET_TEACHER_LANGUAGES_MUTATION)
  const [unlink] = useMutation(UNLINK_TEACHER_MUTATION)
  const [code, setCode] = useState('')
  const [picked, setPicked] = useState([])   // languages for the teacher being added
  const [msg, setMsg] = useState('')
  const links = data?.myTeacherLinks || []
  // Only languages already on your profile — a teaching link is scoped to what
  // you are actually studying, and this is also where the names come from.
  const mine = (learning || []).filter(l => l && l.code)

  const toggle = (c) => { setMsg(''); setPicked(p => (p.includes(c) ? p.filter(x => x !== c) : [...p, c])) }

  const add = async () => {
    setMsg('')
    if (picked.length === 0) { setMsg('Pick which language you learn with them.'); return }
    try {
      const r = await link({ variables: { code: code.trim(), langs: picked } })
      setMsg(`Added ${r.data?.linkTeacherByCode} ✓`)
      setCode(''); setPicked([]); refetch()
    } catch (e) { setMsg(e.message) }
  }
  // Granting and revoking are the same call: the new list replaces the old.
  const grant = async (teacher, current, c) => {
    const next = current.includes(c) ? current.filter(x => x !== c) : [...current, c]
    setMsg('')
    try { await setLangs({ variables: { teacher, langs: next } }); refetch() } catch (e) { setMsg(e.message) }
  }
  const drop = async (t) => {
    if (!window.confirm(`Remove ${t} as your teacher? They will no longer be able to set you homework or see your progress.`)) return
    try { await unlink({ variables: { teacher: t } }); refetch() } catch (e) { setMsg(e.message) }
  }

  return (
    <div style={linkCard}>
      <div style={sectionLabel}>My teachers</div>
      {links.length === 0
        ? <div style={{ color: '#8a8a95', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Ask your teacher for their teaching code and enter it below — then they can set you homework and see your progress in the language you tick.</div>
        : links.map(t => (
          <div key={t.username} style={{ ...personRow, flexWrap: 'wrap' }}>
            <span style={{ color: '#e8e8ec', flex: '1 1 140px' }}>{t.username}</span>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              {mine.map(l => (
                <button key={l.code} onClick={() => grant(t.username, t.langs, l.code)}
                  title={t.langs.includes(l.code) ? `${t.username} can see your ${l.name}` : `${t.username} cannot see your ${l.name}`}
                  style={langChip(t.langs.includes(l.code))}>{l.name}</button>
              ))}
            </div>
            <button style={btn} onClick={() => drop(t.username)}>Remove</button>
          </div>
        ))}
      {links.some(t => t.langs.length === 0) && (
        <div style={{ color: '#fbbf24', fontSize: '0.82rem', marginTop: '0.5rem', lineHeight: 1.5 }}>
          A teacher with no language ticked sees nothing at all. Tick the one you learn with them.
        </div>
      )}

      <div style={{ marginTop: '0.8rem', paddingTop: '0.7rem', borderTop: '1px solid #1f1f26' }}>
        <div style={{ color: '#8a8a95', fontSize: '0.8rem', marginBottom: '0.4rem' }}>Add a teacher — tick what you learn with them:</div>
        {mine.length === 0
          ? <div style={{ color: '#8a8a95', fontSize: '0.85rem' }}>Set a language you are learning on your Profile first.</div>
          : (
            <>
              <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.55rem' }}>
                {mine.map(l => (
                  <button key={l.code} onClick={() => toggle(l.code)} style={langChip(picked.includes(l.code))}>{l.name}</button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                <input value={code} onChange={e => setCode(e.target.value)} placeholder="ABC-123" spellCheck="false" autoComplete="off"
                  onKeyDown={e => { if (e.key === 'Enter' && code.trim()) add() }}
                  style={{ background: '#0d0d0f', border: '1px solid #2a2a33', borderRadius: 9, color: '#e8e8ec', padding: '0.45rem 0.7rem', fontFamily: 'monospace', fontSize: '1rem', letterSpacing: '0.12em', width: '9rem', textTransform: 'uppercase' }} />
                <button style={primary} onClick={add} disabled={loading || !code.trim()}>{loading ? 'Adding…' : 'Add teacher'}</button>
              </div>
            </>
          )}
        {msg && <div style={{ color: msg.endsWith('✓') ? '#6ee7b7' : '#f87171', fontSize: '0.85rem', marginTop: '0.45rem' }}>{msg}</div>}
      </div>
    </div>
  )
}

// ── The teacher's side: your code, and the students who have used it ─────────
function MyTeachCode({ onOpenStudent }) {
  const { data, loading, refetch: refetchCode } = useQuery(MY_TEACH_CODE_QUERY, { variables: {}, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const students = useQuery(MY_STUDENTS_QUERY, { variables: {}, fetchPolicy: 'network-only', errorPolicy: 'ignore' })
  const [regen] = useMutation(REGENERATE_TEACH_CODE_MUTATION)
  const [removeStudent] = useMutation(REMOVE_STUDENT_MUTATION)
  const [msg, setMsg] = useState('')
  // Either we have the real code or we have nothing. Never show a placeholder
  // that looks like a code: someone would copy it, send it on, and both sides
  // would be left thinking the code was broken.
  const code = data?.myTeachCode || ''
  const list = students.data?.myStudents || []

  const copy = async () => {
    try { await navigator.clipboard.writeText(code); setMsg('Copied ✓') } catch { setMsg('Copy it by hand: ' + code) }
  }
  const fresh = async () => {
    if (!window.confirm('Make a new code? The old one stops working straight away. Students already added stay added.')) return
    setMsg('')
    try { await regen(); refetchCode() } catch (e) { setMsg(e.message) }
  }
  const drop = async (s) => {
    if (!window.confirm(`Remove ${s} from your students? Homework you already set stays with them.`)) return
    try { await removeStudent({ variables: { student: s } }); students.refetch() } catch (e) { setMsg(e.message) }
  }

  return (
    <div style={linkCard}>
      <div style={sectionLabel}>Your teaching code</div>
      {code ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '1.5rem', letterSpacing: '0.18em', color: '#6ee7b7' }}>{code}</span>
            <button style={btn} onClick={copy}>Copy</button>
            <button style={btn} onClick={fresh}>New code</button>
            {msg && <span style={{ color: msg.startsWith('Copied') ? '#6ee7b7' : '#f87171', fontSize: '0.85rem' }}>{msg}</span>}
          </div>
          <div style={{ color: '#8a8a95', fontSize: '0.82rem', marginTop: '0.4rem' }}>
            Send this to a student. Once they enter it under <em>My homework</em>, you can assign them lessons.
          </div>
        </>
      ) : loading ? (
        <div style={{ color: '#8a8a95', fontSize: '0.85rem' }}>Getting your code…</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
          <span style={{ color: '#fbbf24', fontSize: '0.85rem' }}>Your code couldn't be loaded — the server didn't answer.</span>
          <button style={btn} onClick={() => refetchCode()}>Try again</button>
        </div>
      )}
      <div style={{ ...sectionLabel, marginTop: '0.9rem' }}>Students ({list.length})</div>
      {list.length === 0
        ? <div style={{ color: '#8a8a95', fontSize: '0.85rem' }}>No one has used your code yet.</div>
        : list.map(s => (
          <div key={s.username} style={personRow}>
            <button
              style={{ ...btn, background: 'none', border: 'none', padding: 0, flex: 1, textAlign: 'left', color: '#e8e8ec', fontSize: '0.95rem' }}
              onClick={() => onOpenStudent(s.username)}
              title={`See what ${s.username} has been doing`}
            >
              {s.username} <span style={{ color: '#6ee7b7', fontSize: '0.8rem' }}>· progress ›</span>
            </button>
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              {s.langs.length === 0
                ? <span style={{ color: '#fbbf24', fontSize: '0.76rem' }}>no language shared yet</span>
                : s.langs.map(c => <span key={c} style={{ ...langChip(true), cursor: 'default' }}>{c}</span>)}
            </div>
            <button style={btn} onClick={() => drop(s.username)}>Remove</button>
          </div>
        ))}
    </div>
  )
}

// One shared practice set: the score, the sentences that needed help, and a
// note back to the student.
function DrillRow({ d, onChanged }) {
  const [open, setOpen] = useState(false)
  const [comment, setComment] = useState(d.comment || '')
  const [msg, setMsg] = useState('')
  const [send, { loading }] = useMutation(COMMENT_DRILL_RESULT_MUTATION)
  const save = async () => {
    setMsg('')
    try { await send({ variables: { resultId: d.id, comment } }); setMsg('Sent ✓'); onChanged() }
    catch (e) { setMsg(e.message) }
  }
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', cursor: 'pointer', flexWrap: 'wrap' }} onClick={() => setOpen(o => !o)}>
        <span style={{ color: '#e8e8ec', flex: 1, minWidth: 160 }}><b>{d.student}</b> · {d.title}</span>
        <span style={{ color: pctColor(d.pct), fontWeight: 700 }}>{d.pct}%</span>
        <span style={{ color: '#8a8a95', fontSize: '0.78rem' }}>{d.perfect}/{d.total} first time</span>
        <span style={{ color: '#8a8a95' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ color: '#8a8a95', fontSize: '0.78rem' }}>{fmtTime(d.sharedAt)}{d.language ? ` · ${d.language}` : ''}{d.mode ? ` · ${drillModeLabel(d.mode)}` : ''}</div>
          {/* Case drills and Verb Lab sets store different details; this renders either. */}
          <DrillMisses details={d.details} />
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} placeholder="Note back to the student…"
            style={{ width: '100%', boxSizing: 'border-box', background: '#0d0d0f', border: '1px solid #2a2a33', borderRadius: 10, color: '#e8e8ec', padding: '0.5rem 0.7rem', fontFamily: 'inherit', fontSize: '0.9rem' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
            <button style={primary} onClick={save} disabled={loading || !comment.trim()}>{loading ? 'Sending…' : 'Send note'}</button>
            {d.reviewedAt && <span style={{ color: '#8a8a95', fontSize: '0.8rem' }}>noted {fmtTime(d.reviewedAt)}</span>}
            {msg && <span style={{ color: msg.endsWith('✓') ? '#6ee7b7' : '#f87171', fontSize: '0.85rem' }}>{msg}</span>}
          </div>
        </div>
      )}
    </div>
  )
}

function StudentRow({ row, meetingId, onChanged }) {
  const [open, setOpen] = useState(false)
  const [comment, setComment] = useState(row.submission?.comment || '')
  const [msg, setMsg] = useState('')
  const [review, { loading }] = useMutation(REVIEW_SUBMISSION_MUTATION)
  const [unassign] = useMutation(UNASSIGN_LESSON_MUTATION)
  const sub = row.submission
  const send = async () => {
    if (!sub) return
    setMsg('')
    try { await review({ variables: { submissionId: sub.id, comment } }); setMsg('Feedback sent ✓'); onChanged() }
    catch (e) { setMsg(e.message) }
  }
  const remove = async () => {
    if (!window.confirm(`Remove this homework for ${row.student}?`)) return
    try { await unassign({ variables: { meetingId, student: row.student } }); onChanged() } catch (e) { setMsg(e.message) }
  }
  return (
    <div style={{ borderTop: '1px solid #1f1f26', padding: '0.5rem 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <span style={{ color: '#e8e8ec', flex: 1 }}>{row.student}</span>
        {sub?.updatedAt && <span style={{ color: '#8a8a95', fontSize: '0.78rem' }}>{fmtTime(sub.updatedAt)}</span>}
        <Chip status={row.status} />
        <span style={{ color: '#8a8a95' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {!sub ? <div style={{ color: '#8a8a95', fontSize: '0.85rem' }}>No answers yet.</div>
            : (sub.answers || []).map(a => (
              <div key={a.index} style={{ background: '#0d0d0f', border: '1px solid #2a2a33', borderRadius: 10, padding: '0.5rem 0.7rem' }}>
                <div style={{ color: '#8a8a95', fontSize: '0.8rem' }}>{a.index + 1}. {a.prompt}</div>
                <div style={{ color: a.text ? '#e8e8ec' : '#555560', marginTop: '0.2rem' }}>{a.text || '— no answer —'}</div>
              </div>
            ))}
          {sub && (
            <>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} placeholder="Feedback for the student…"
                style={{ width: '100%', boxSizing: 'border-box', background: '#0d0d0f', border: '1px solid #2a2a33', borderRadius: 10, color: '#e8e8ec', padding: '0.5rem 0.7rem', fontFamily: 'inherit', fontSize: '0.9rem' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
                <button style={primary} onClick={send} disabled={loading || !comment.trim()}>{loading ? 'Sending…' : 'Send feedback'}</button>
                <button style={btn} onClick={remove}>Remove homework</button>
                {msg && <span style={{ color: msg.endsWith('✓') ? '#6ee7b7' : '#f87171', fontSize: '0.85rem' }}>{msg}</span>}
              </div>
            </>
          )}
          {!sub && <button style={btn} onClick={remove}>Remove homework</button>}
        </div>
      )}
    </div>
  )
}
